import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { useFilterParams } from '../../hooks';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import { reservationAPI } from '../../services';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import DateRangePicker, { calcDatePreset } from '../../components/fields/DateRangePicker';

// ===== Nhãn & màu trạng thái =====
const STATUS_LABELS = {
  PENDING: 'Chờ đến',
  SEATED: 'Đã vào bàn',
  CANCELLED: 'Đã huỷ',
  NO_SHOW: 'Không đến',
};
const STATUS_VARIANT = {
  PENDING: 'warning',
  SEATED: 'success',
  CANCELLED: 'danger',
  NO_SHOW: 'secondary',
};

const CONFIRM_LABELS = {
  UNCONFIRMED: 'Chưa phản hồi',
  CONFIRMED: 'Đã chốt đến',
  RESCHEDULE_REQUESTED: 'Xin đổi giờ',
  CANCELLED_BY_CUSTOMER: 'Khách huỷ',
};
const CONFIRM_VARIANT = {
  UNCONFIRMED: 'secondary',
  CONFIRMED: 'success',
  RESCHEDULE_REQUESTED: 'warning',
  CANCELLED_BY_CUSTOMER: 'danger',
};

const SOURCE_LABELS = {
  STAFF: 'Nhân viên',
  TELEGRAM: 'Telegram',
  WEB: 'Web (khách)',
  ZALO: 'Zalo (khách)',
};

const ARRIVAL_LABELS = {
  PENDING_CHECK: 'Chưa check',
  ARRIVED: 'Đã đến',
  NO_SHOW: 'Không đến',
};

const ROLE_LABELS = {
  ADMIN: 'Admin',
  MANAGER: 'Quản lý',
  STAFF: 'Nhân viên',
};

// ===== Helpers =====
const fmtDateTime = (iso) => {
  if (!iso) return '-';
  try {
    return format(new Date(iso), 'dd/MM/yyyy HH:mm');
  } catch {
    return '-';
  }
};
const fmtTime = (iso) => {
  if (!iso) return '-';
  try {
    return format(new Date(iso), 'HH:mm');
  } catch {
    return '-';
  }
};

const staffName = (emp) => {
  if (!emp) return null;
  const roleLabel = ROLE_LABELS[emp.role] || emp.role || '';
  return roleLabel ? `${emp.full_name} (${roleLabel})` : emp.full_name;
};

/** Suy ra "ai huỷ / no-show" từ dữ liệu có sẵn (không cần field riêng). */
const deriveOutcome = (r) => {
  if (r.customer_confirm_status === 'CANCELLED_BY_CUSTOMER') {
    return { text: 'Khách tự huỷ', variant: 'warning', at: r.customer_confirmed_at };
  }
  if (r.status === 'NO_SHOW') {
    const who = r.arrivalChecker ? `NV ${r.arrivalChecker.full_name} xác nhận` : 'hệ thống';
    return { text: `No-show — ${who}`, variant: 'secondary', at: r.arrival_checked_at };
  }
  if (r.status === 'CANCELLED') {
    if (r.statusChanger) {
      const isMgr = ['ADMIN', 'MANAGER'].includes(r.statusChanger.role);
      const prefix = isMgr ? 'Admin/QL' : 'NV';
      return {
        text: `${prefix} ${r.statusChanger.full_name} huỷ`,
        variant: 'danger',
        at: r.updated_at,
      };
    }
    return { text: 'Hệ thống / không rõ', variant: 'dark', at: r.updated_at };
  }
  return null; // chưa huỷ
};

/* ───────────────── Catalog tiêu chí chuẩn bị bàn (per-branch) ───────────────── */
const PrepCriteriaCatalog = ({ branchId }) => {
  const [items, setItems] = useState([]);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [critical, setCritical] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!branchId) {
      setItems([]);
      return;
    }
    try {
      const res = await reservationAPI.listPrepCriteria(branchId);
      const payload = res?.data ?? res;
      setItems(Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []);
    } catch {
      setItems([]);
    }
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const addItem = async () => {
    if (!code.trim() || !label.trim()) {
      toast.error('Nhập đủ mã và nhãn tiêu chí');
      return;
    }
    try {
      setSaving(true);
      await reservationAPI.createPrepCriterion({
        branch_id: branchId,
        code: code.trim().toUpperCase(),
        label: label.trim(),
        is_critical: critical,
        sort_order: items.length + 1,
      });
      setCode('');
      setLabel('');
      setCritical(false);
      toast.success('Đã thêm tiêu chí');
      load();
    } catch (err) {
      toast.error(err?.message || 'Không thêm được tiêu chí');
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (id) => {
    if (!window.confirm('Xóa tiêu chí này?')) return;
    try {
      await reservationAPI.deletePrepCriterion(id);
      toast.success('Đã xóa');
      load();
    } catch (err) {
      toast.error(err?.message || 'Không xóa được');
    }
  };

  return (
    <Card className="shadow-sm mb-3">
      <Card.Header className="bg-light">
        <strong>Tiêu chí chuẩn bị bàn (catalog)</strong>
        <span className="text-muted small"> — hướng dẫn NV chụp + QC review. Để trống dùng mặc định.</span>
      </Card.Header>
      <Card.Body>
        <div className="d-flex flex-wrap gap-2 mb-3">
          {items.length === 0 && (
            <span className="text-muted small">Chưa có tiêu chí riêng — đang dùng mặc định hệ thống.</span>
          )}
          {items.map((c) => (
            <Badge key={c.id} bg="light" text="dark" className="border d-flex align-items-center gap-2 py-2 px-3">
              <span className="fw-semibold">{c.label}</span>
              <code className="small text-muted">{c.code}</code>
              {c.is_critical && <Badge bg="danger">Quan trọng</Badge>}
              <Button variant="link" size="sm" className="p-0 text-danger" onClick={() => removeItem(c.id)}>✕</Button>
            </Badge>
          ))}
        </div>
        <Row className="g-2 align-items-end">
          <Col xs={4} md={3}>
            <Form.Label className="small mb-1">Mã</Form.Label>
            <Form.Control size="sm" placeholder="CLEAN" value={code} onChange={(e) => setCode(e.target.value)} />
          </Col>
          <Col xs={5} md={4}>
            <Form.Label className="small mb-1">Nhãn</Form.Label>
            <Form.Control size="sm" placeholder="Bàn & ghế sạch" value={label} onChange={(e) => setLabel(e.target.value)} />
          </Col>
          <Col xs="auto">
            <Form.Check
              type="checkbox"
              label="Quan trọng"
              checked={critical}
              onChange={(e) => setCritical(e.target.checked)}
            />
          </Col>
          <Col xs="auto">
            <Button size="sm" variant="primary" onClick={addItem} disabled={saving || !branchId}>
              Thêm
            </Button>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

// #120936 — deep-link cho màn đặt bàn.
// `branch_id` do BranchContext quản (không khai lại ở đây). Trang này lọc client-side nên KHÔNG
// có `page` để đưa lên URL.
// CỐ Ý KHÔNG đưa `search` lên URL: ô này tìm theo TÊN và SỐ ĐIỆN THOẠI khách — luật mục 7.2 của
// KHUON-CHUAN-DEEPLINK.md cấm đưa dữ liệu cá nhân vào param, vì URL lọt vào lịch sử trình duyệt,
// log server/proxy và ảnh chụp màn hình gửi Telegram. Cần tới đích danh thì dùng id.
const RESERVATION_STATUSES = ['PENDING', 'SEATED', 'CANCELLED', 'NO_SHOW'];
const CONFIRM_STATUSES = ['UNCONFIRMED', 'CONFIRMED', 'RESCHEDULE_REQUESTED', 'CANCELLED_BY_CUSTOMER'];
const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));

const RESERVATION_FILTER_SPEC = {
  from: { default: () => calcDatePreset('last7days').from, valid: isIsoDate },
  to: { default: () => calcDatePreset('last7days').to, valid: isIsoDate },
  status: {
    default: '',
    parse: (v) => v.toUpperCase(),
    valid: (v) => RESERVATION_STATUSES.includes(v.toUpperCase()),
  },
  confirm: {
    default: '',
    parse: (v) => v.toUpperCase(),
    valid: (v) => CONFIRM_STATUSES.includes(v.toUpperCase()),
  },
  /**
   * #191439 — `?id=` MỞ THẲNG modal chi tiết của đúng phiếu đặt bàn (khuôn
   * `KHUON-CHUAN-URL-PARAM.md` mục 1: "`id` là tham số ăn tiền nhất"). Dùng bởi 2 tin: khách
   * chưa đến phải xác nhận (dòng 80) và cảnh báo spam đặt bàn (dòng 98) — cả hai đều là việc
   * gấp theo phút, bắt người nhận tự dò trong danh sách là mất đúng thứ link sinh ra để cứu.
   */
  id: { default: '' },
};

const ReservationManagementPage = () => {
  const { selectedBranch } = useBranch();

  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] =
    useFilterParams(RESERVATION_FILTER_SPEC);
  // `search` giữ ở state cục bộ, không lên URL (xem ghi chú trên).
  const [search, setSearch] = useState('');

  const filters = useMemo(() => ({ ...urlFilters, search }), [urlFilters, search]);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Bộ điều hợp giữ nguyên cách gọi cũ `setFilters(patch | (prev) => next)` của các chỗ trong
  // trang, rồi tách: `search` về state cục bộ, phần còn lại lên URL.
  const setFilters = useCallback(
    (patch) => {
      const next = typeof patch === 'function' ? patch(filtersRef.current) : patch;
      const { search: nextSearch, ...rest } = next;
      if (nextSearch !== undefined && nextSearch !== filtersRef.current.search) setSearch(nextSearch);

      const changed = Object.fromEntries(
        Object.entries(rest).filter(([k, v]) => v !== filtersRef.current[k]),
      );
      if (Object.keys(changed).length) setUrlFilters(changed);
    },
    [setUrlFilters],
  );
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  // #191439: chỉ tự mở modal MỘT lần cho mỗi `?id=`. Không có cờ này thì người dùng đóng
  // modal xong sẽ bị mở lại ngay ở lần render kế tiếp — không thoát ra được.
  const autoOpenedIdRef = useRef(null);

  // QC review state
  const [prepCriteria, setPrepCriteria] = useState([]);
  const [qcResults, setQcResults] = useState({});
  const [qcNotes, setQcNotes] = useState('');
  const [qcSubmitting, setQcSubmitting] = useState(false);

  const fetchReservations = useCallback(async () => {
    if (!selectedBranch?.id) {
      setReservations([]);
      return;
    }
    if (!filters.from || !filters.to) return; // tránh Invalid Date khi xoá ô ngày
    try {
      setLoading(true);
      // from/to (YYYY-MM-DD) → bao trọn ngày theo local rồi gửi ISO
      const fromISO = new Date(`${filters.from}T00:00:00`).toISOString();
      const toISO = new Date(`${filters.to}T23:59:59`).toISOString();
      const response = await reservationAPI.getReservations({
        branchId: selectedBranch.id,
        from: fromISO,
        to: toISO,
      });
      const payload = response?.data || response;
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      setReservations(list);
      // Sync record đang mở trong modal (nếu có) để không hiển thị dữ liệu cũ
      setDetail((prev) => (prev ? list.find((r) => r.id === prev.id) ?? prev : prev));
    } catch (error) {
      toast.error(error?.message || 'Không tải được danh sách đặt bàn');
    } finally {
      setLoading(false);
    }
  }, [selectedBranch, filters.from, filters.to]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  // #191439: link sâu có `?id=` thì mở thẳng modal chi tiết của đúng phiếu đó, ngay khi danh
  // sách vừa tải xong.
  //
  // #210029 (Kiểm thử #202611 lỗi Nặng 1): bản cũ chỉ tìm trong danh sách ĐANG lọc, nên phiếu
  // nằm ngoài khoảng ngày mặc định ("7 ngày trước") thì im lặng không mở gì. Ca thật gặp phải:
  // đặt bàn 21/08 mở link ngày 29/08 → không có modal nào, người nhận tưởng link hỏng. Mà đây
  // đúng là ca link sâu sinh ra để cứu (khách không đến / nghi spam), tin Telegram nằm lại
  // trong nhóm nhiều ngày là chuyện bình thường.
  //
  // Nay: không thấy trong danh sách thì HỎI THẲNG API theo id (`GET /reservations/:id`), mở
  // modal, và kéo luôn khoảng ngày về đúng ngày của phiếu để dòng đó hiện trong bảng phía sau
  // (luật link sâu: "đã lọc sẵn, không phải bấm thêm bước lọc nào"). Id sai/không có quyền thì
  // báo một dòng nhìn thấy được rồi thôi — không chặn, không trắng màn.
  useEffect(() => {
    const wantedId = urlFilters.id;
    if (!wantedId || loading || autoOpenedIdRef.current === wantedId) return;

    const found = reservations.find((r) => r.id === wantedId);
    if (found) {
      autoOpenedIdRef.current = wantedId;
      setDetail(found);
      return;
    }

    // Đánh dấu NGAY trước khi gọi API: effect này chạy lại mỗi lần `reservations` đổi (kể cả do
    // chính việc đổi khoảng ngày bên dưới), không đánh dấu sớm là gọi API lặp vô hạn.
    autoOpenedIdRef.current = wantedId;
    reservationAPI
      .getReservationById(wantedId)
      .then((res) => {
        const record = res?.data?.id ? res.data : res;
        if (!record?.id) return;
        setDetail(record);
        // Ngoài khoảng ngày đang lọc → mở rộng bộ lọc tới đúng ngày của phiếu.
        const ngay = record.reservation_time
          ? format(new Date(record.reservation_time), 'yyyy-MM-dd')
          : null;
        if (ngay && (ngay < filtersRef.current.from || ngay > filtersRef.current.to)) {
          setFilters({
            from: ngay < filtersRef.current.from ? ngay : filtersRef.current.from,
            to: ngay > filtersRef.current.to ? ngay : filtersRef.current.to,
          });
        }
      })
      .catch(() => {
        toast.warn('Không mở được phiếu đặt bàn theo đường dẫn (id không tồn tại hoặc ngoài quyền xem).');
      });
    // CỐ Ý không có hàm dọn "huỷ": effect này chạy lại mỗi lần danh sách tải xong, dọn kiểu
    // `let huy = true` sẽ huỷ đúng lần gọi API đang chờ và modal không bao giờ mở.
  }, [urlFilters.id, reservations, loading, setFilters]);

  // Filter client-side (status / confirm / search tên+SĐT)
  const filtered = useMemo(() => {
    const kw = filters.search.trim().toLowerCase();
    return reservations.filter((r) => {
      if (filters.status && r.status !== filters.status) return false;
      if (filters.confirm && r.customer_confirm_status !== filters.confirm) return false;
      if (kw) {
        const hay = `${r.customer_name || ''} ${r.customer_phone || ''}`.toLowerCase();
        if (!hay.includes(kw)) return false;
      }
      return true;
    });
  }, [reservations, filters.status, filters.confirm, filters.search]);

  const summary = useMemo(() => {
    const total = filtered.length;
    const arrived = filtered.filter(
      (r) => r.status === 'SEATED' || r.customer_arrival_status === 'ARRIVED'
    ).length;
    const noShow = filtered.filter((r) => r.status === 'NO_SHOW').length;
    const cancelled = filtered.filter((r) => r.status === 'CANCELLED').length;
    const pending = filtered.filter((r) => r.status === 'PENDING').length;
    return { total, arrived, noShow, cancelled, pending };
  }, [filtered]);

  const handleDateChange = (from, to) => {
    setFilters((prev) => ({ ...prev, from, to }));
  };
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };
  const handleReset = () => {
    const r = calcDatePreset('last7days');
    setFilters({ from: r.from, to: r.to, status: '', confirm: '', search: '' });
  };

  // Fetch tiêu chí + prefill khi mở detail có prep
  useEffect(() => {
    if (!detail?.prepared_at) return;
    reservationAPI.getPrepCriteria(detail.branch_id).then((data) => {
      const list = Array.isArray(data) ? data : [];
      setPrepCriteria(list);
      // Prefill từ kết quả QC đã lưu
      const saved = detail.prep_qc_results || {};
      const prefill = {};
      list.forEach((c) => { prefill[c.code] = saved[c.code] ?? null; });
      setQcResults(prefill);
      setQcNotes(detail.prep_qc_notes || '');
    }).catch(() => setPrepCriteria([]));
  }, [detail?.id, detail?.prepared_at]);

  const handleQcToggle = (code, value) => {
    setQcResults((prev) => ({ ...prev, [code]: value }));
  };

  const handleSubmitQcReview = async () => {
    if (!detail) return;
    try {
      setQcSubmitting(true);
      await reservationAPI.reviewPrep(detail.id, { criteria_results: qcResults, notes: qcNotes });
      toast.success('Đã lưu kết quả QC');
      fetchReservations();
    } catch (err) {
      toast.error(err?.message || 'Không thể lưu QC');
    } finally {
      setQcSubmitting(false);
    }
  };

  const renderSpace = (r) => {
    if (r.space_type === 'PRIVATE') {
      return `Phòng riêng${r.room_name ? ` (${r.room_name})` : ''}`;
    }
    return r.table?.number ? `Bàn ${r.table.number}` : 'Chung';
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <div className="mc-page-header my-2">
          <div className="mc-page-header-title">
            <h1>Quản lý đặt bàn</h1>
            <small className="text-muted">
              {selectedBranch?.name ? `Chi nhánh: ${selectedBranch.name}` : 'Vui lòng chọn chi nhánh'}
            </small>
          </div>
          <div className="mc-page-header-actions d-flex gap-2">
            <Button variant="outline-secondary" onClick={handleReset} disabled={loading}>
              Đặt lại bộ lọc
            </Button>
            <Button variant="primary" onClick={fetchReservations} disabled={loading}>
              {loading ? 'Đang tải...' : 'Làm mới'}
            </Button>
          </div>
        </div>

        {/* Summary cards */}
        <Row className="mb-3">
          <Col md className="mb-3">
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <small className="text-muted text-uppercase">Tổng đặt bàn</small>
                <h3 className="mb-0">{summary.total}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md className="mb-3">
            <Card className="h-100 shadow-sm border-success">
              <Card.Body>
                <small className="text-muted text-uppercase">Khách đã đến</small>
                <h3 className="text-success mb-0">{summary.arrived}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md className="mb-3">
            <Card className="h-100 shadow-sm border-warning">
              <Card.Body>
                <small className="text-muted text-uppercase">Đang chờ</small>
                <h3 className="text-warning mb-0">{summary.pending}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md className="mb-3">
            <Card className="h-100 shadow-sm border-secondary">
              <Card.Body>
                <small className="text-muted text-uppercase">No-show</small>
                <h3 className="text-secondary mb-0">{summary.noShow}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md className="mb-3">
            <Card className="h-100 shadow-sm border-danger">
              <Card.Body>
                <small className="text-muted text-uppercase">Đã huỷ</small>
                <h3 className="text-danger mb-0">{summary.cancelled}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Catalog tiêu chí chuẩn bị bàn */}
        <PrepCriteriaCatalog branchId={selectedBranch?.id} />

        {/* Filters */}
        <Card className="mb-3 shadow-sm">
          <Card.Body>
            <InvalidParamsNotice
              ignored={ignored}
              onDismiss={dismissIgnored}
            />
            <DateRangePicker
              fromDate={filters.from}
              toDate={filters.to}
              onChange={handleDateChange}
              defaultPreset="last7days"
            />
            <Row>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Trạng thái</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <option value="">Tất cả</option>
                    {Object.keys(STATUS_LABELS).map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Khách chốt đến</Form.Label>
                  <Form.Select
                    size="sm"
                    value={filters.confirm}
                    onChange={(e) => handleFilterChange('confirm', e.target.value)}
                  >
                    <option value="">Tất cả</option>
                    {Object.keys(CONFIRM_LABELS).map((c) => (
                      <option key={c} value={c}>
                        {CONFIRM_LABELS[c]}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Tìm tên / SĐT khách</Form.Label>
                  <Form.Control
                    size="sm"
                    type="text"
                    placeholder="Nhập tên hoặc số điện thoại..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Table */}
        <Card className="shadow-sm">
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center text-muted py-5">Không có đặt bàn nào trong khoảng này.</div>
            ) : (
              <Table striped hover responsive className="mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Khách</th>
                    <th>Giờ đến</th>
                    <th>Không gian</th>
                    <th>Nguồn / Người tạo</th>
                    <th>Khách chốt đến?</th>
                    <th>Đã gửi tin?</th>
                    <th>Chuẩn bị bàn</th>
                    <th>Trạng thái</th>
                    <th>Ai huỷ / No-show</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const outcome = deriveOutcome(r);
                    return (
                      <tr
                        key={r.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setDetail(r)}
                      >
                        <td>
                          <div className="fw-semibold">{r.customer_name || '-'}</div>
                          <small className="text-muted">
                            {r.customer_phone || 'Không SĐT'} · {r.guest_count} khách
                          </small>
                        </td>
                        <td>{fmtDateTime(r.reservation_time)}</td>
                        <td>{renderSpace(r)}</td>
                        <td>
                          <Badge bg="info" className="me-1">
                            {SOURCE_LABELS[r.source] || r.source}
                          </Badge>
                          <div>
                            <small>{r.creator?.full_name || 'Khách tự đặt'}</small>
                          </div>
                        </td>
                        <td>
                          <Badge bg={CONFIRM_VARIANT[r.customer_confirm_status] || 'secondary'}>
                            {CONFIRM_LABELS[r.customer_confirm_status] || r.customer_confirm_status}
                          </Badge>
                          {r.customer_confirmed_at && (
                            <div>
                              <small className="text-muted">{fmtTime(r.customer_confirmed_at)}</small>
                            </div>
                          )}
                        </td>
                        <td>
                          {r.customer_confirm_sent_at ? (
                            <span className="text-success">Đã gửi {fmtTime(r.customer_confirm_sent_at)}</span>
                          ) : (
                            <span className="text-muted">Chưa gửi</span>
                          )}
                        </td>
                        <td>
                          {r.prepared_at ? (
                            <>
                              <div>
                                <small>{r.preparer?.full_name || '—'}</small>
                              </div>
                              <small className="text-muted">{fmtTime(r.prepared_at)}</small>
                              {r.prep_photo_url && (
                                <a
                                  href={r.prep_photo_url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="ms-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  ảnh
                                </a>
                              )}
                            </>
                          ) : (
                            <span className="text-muted">Chưa chuẩn bị</span>
                          )}
                        </td>
                        <td>
                          <Badge bg={STATUS_VARIANT[r.status] || 'secondary'}>
                            {STATUS_LABELS[r.status] || r.status}
                          </Badge>
                        </td>
                        <td>
                          {outcome ? (
                            <Badge bg={outcome.variant}>{outcome.text}</Badge>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Detail modal — timeline đầy đủ vòng đời */}
      <Modal show={!!detail} onHide={() => setDetail(null)} size="lg" centered>
        {detail && (
          <>
            <Modal.Header closeButton>
              <Modal.Title>
                Đặt bàn — {detail.customer_name}{' '}
                <Badge bg={STATUS_VARIANT[detail.status] || 'secondary'}>
                  {STATUS_LABELS[detail.status] || detail.status}
                </Badge>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <div className="text-muted small">Khách</div>
                  <div>
                    {detail.customer_name} · {detail.customer_phone || 'Không SĐT'} · {detail.guest_count} khách
                  </div>
                </Col>
                <Col md={6}>
                  <div className="text-muted small">Giờ đến / Không gian</div>
                  <div>
                    {fmtDateTime(detail.reservation_time)} · {renderSpace(detail)}
                    {detail.duration_minutes ? ` · ${detail.duration_minutes} phút` : ''}
                  </div>
                </Col>
              </Row>
              {detail.notes && (
                <div className="mb-3">
                  <div className="text-muted small">Ghi chú</div>
                  <div>{detail.notes}</div>
                </div>
              )}

              <h6 className="mt-2">Diễn biến</h6>
              <Table size="sm" bordered className="mb-0">
                <tbody>
                  <tr>
                    <td className="text-muted" style={{ width: 200 }}>Tạo đặt bàn</td>
                    <td>
                      {fmtDateTime(detail.created_at)} · {SOURCE_LABELS[detail.source] || detail.source}
                      {' · '}
                      {staffName(detail.creator) || 'Khách tự đặt'}
                      {detail.edit_count ? ` · sửa ${detail.edit_count} lần` : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Gửi tin cho khách</td>
                    <td>
                      {detail.customer_confirm_sent_at
                        ? `Đã gửi link xác nhận lúc ${fmtDateTime(detail.customer_confirm_sent_at)}`
                        : 'Chưa gửi link xác nhận'}
                      {detail.reminder_sent_at
                        ? ` · Nhắc NV chuẩn bị: ${fmtDateTime(detail.reminder_sent_at)}`
                        : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Khách phản hồi</td>
                    <td>
                      <Badge bg={CONFIRM_VARIANT[detail.customer_confirm_status] || 'secondary'}>
                        {CONFIRM_LABELS[detail.customer_confirm_status] || detail.customer_confirm_status}
                      </Badge>
                      {detail.customer_confirmed_at ? ` · ${fmtDateTime(detail.customer_confirmed_at)}` : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Nhân viên chuẩn bị bàn</td>
                    <td>
                      {detail.prepared_at ? (
                        <>
                          {staffName(detail.preparer) || '—'} · {fmtDateTime(detail.prepared_at)}
                          {detail.prep_photo_url && (
                            <>
                              {' · '}
                              <a href={detail.prep_photo_url} target="_blank" rel="noreferrer">
                                xem ảnh
                              </a>
                            </>
                          )}
                          {detail.prep_qc_reviewed_at && (
                            <span className="ms-2 text-success small">
                              ✓ Đã QC · {fmtDateTime(detail.prep_qc_reviewed_at)}
                            </span>
                          )}
                        </>
                      ) : (
                        'Chưa chuẩn bị'
                      )}
                    </td>
                  </tr>
                  {detail.prepared_at && prepCriteria.length > 0 && (
                    <tr>
                      <td className="text-muted align-top">QC chuẩn bị bàn</td>
                      <td>
                        {detail.prep_photo_url && (
                          <div className="mb-2">
                            <a href={detail.prep_photo_url} target="_blank" rel="noreferrer">
                              <img
                                src={detail.prep_photo_url}
                                alt="Ảnh chuẩn bị bàn"
                                style={{ maxHeight: 160, maxWidth: '100%', borderRadius: 6, border: '1px solid #dee2e6' }}
                              />
                            </a>
                          </div>
                        )}
                        <div className="mb-2">
                          {prepCriteria.map((c) => (
                            <div key={c.code} className="d-flex align-items-center gap-2 mb-1">
                              <Button
                                size="sm"
                                variant={qcResults[c.code] === true ? 'success' : 'outline-secondary'}
                                style={{ minWidth: 52 }}
                                onClick={() => handleQcToggle(c.code, true)}
                              >
                                Đạt
                              </Button>
                              <Button
                                size="sm"
                                variant={qcResults[c.code] === false ? 'danger' : 'outline-secondary'}
                                style={{ minWidth: 52 }}
                                onClick={() => handleQcToggle(c.code, false)}
                              >
                                KĐ
                              </Button>
                              <span className="small">{c.label}</span>
                            </div>
                          ))}
                        </div>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Ghi chú QC (tuỳ chọn)"
                          value={qcNotes}
                          onChange={(e) => setQcNotes(e.target.value)}
                          className="mb-2"
                          style={{ fontSize: 13 }}
                        />
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={handleSubmitQcReview}
                          disabled={qcSubmitting}
                        >
                          {qcSubmitting ? 'Đang lưu…' : 'Lưu QC'}
                        </Button>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td className="text-muted">Check khách đến</td>
                    <td>
                      {ARRIVAL_LABELS[detail.customer_arrival_status] || detail.customer_arrival_status}
                      {detail.arrival_checked_at ? ` · ${fmtDateTime(detail.arrival_checked_at)}` : ''}
                      {detail.arrivalChecker ? ` · ${staffName(detail.arrivalChecker)}` : ''}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-muted">Kết quả huỷ / no-show</td>
                    <td>
                      {(() => {
                        const o = deriveOutcome(detail);
                        return o ? (
                          <Badge bg={o.variant}>{o.text}</Badge>
                        ) : (
                          <span className="text-muted">Chưa huỷ</span>
                        );
                      })()}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setDetail(null)}>
                Đóng
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </PageLayout>
  );
};

export default ReservationManagementPage;
